import { Box, Stack } from "@mui/material";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import {
  Card,
  Divider,
  Input,
  Text,
  TextButton,
  Title,
} from "@src/appV2/redesign/system/ui-components";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from "react";
import { type Control, Controller, type UseFormSetValue, useWatch } from "react-hook-form";
import SignaturePad from "react-signature-pad-wrapper";

import { type TimesheetFormValues } from "../types/timesheetFormTypes";

interface FacilitySignatureSectionProps {
  control: Control<TimesheetFormValues>;
  isCaliforniaFacility: boolean;
  firstName: string;
  onSubmit: () => void;
  isValid: boolean;
  signaturePadIsDisabled: boolean;
  setValue: UseFormSetValue<TimesheetFormValues>;
  onDisabledSignaturePadTouchStart: () => void;
  submitButtonText?: string;
}

export interface FacilitySignatureSectionRef {
  clearSignature: () => void;
}

export const FacilitySignatureSection = forwardRef<
  FacilitySignatureSectionRef,
  FacilitySignatureSectionProps
>((props, ref) => {
  const {
    control,
    isCaliforniaFacility,
    firstName,
    onSubmit,
    isValid,
    signaturePadIsDisabled,
    setValue,
    onDisabledSignaturePadTouchStart,
    submitButtonText = "Submit",
  } = props;

  const workerStationInputRef = useRef<HTMLInputElement>(null);
  const workerNursingAssignmentInputRef = useRef<HTMLInputElement>(null);
  const signaturePadRef = useRef<SignaturePad>(null);

  const currentSignature = useWatch({
    control,
    name: "facilityEmployeeSignature",
  });

  useEffect(() => {
    if (currentSignature && signaturePadRef.current?.isEmpty()) {
      signaturePadRef.current?.fromDataURL(currentSignature);
    }
  }, [currentSignature]);

  const onSignatureUpdate = useCallback(() => {
    const signature = signaturePadRef.current?.toDataURL();
    if (signature) {
      setValue("facilityEmployeeSignature", signature, { shouldValidate: true });
    }
  }, [setValue]);

  useEffect(() => {
    const signaturePad = signaturePadRef.current;
    signaturePad?.instance?.addEventListener("endStroke", onSignatureUpdate);
    return () => {
      signaturePad?.instance?.removeEventListener("endStroke", onSignatureUpdate);
    };
  }, [onSignatureUpdate]);

  const clearSignature = useCallback(() => {
    signaturePadRef.current?.clear();
    setValue("facilityEmployeeSignature", "", { shouldValidate: true });
  }, [setValue]);

  useImperativeHandle(ref, () => ({
    clearSignature,
  }));

  return (
    <Stack
      spacing={5}
      sx={{
        paddingX: 6,
        paddingY: 6,
        backgroundColor: (theme) => theme.palette.background.paper,
      }}
    >
      <Title bold variant="h6" component="h2">
        Please ask a workplace employee to verify your times and sign
      </Title>

      <Stack spacing={2}>
        <Text variant="body2">Workplace employee name*</Text>
        <Controller
          name="facilityEmployeeName"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              placeholder="Name"
              disabled={signaturePadIsDisabled}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                clearSignature();
                field.onChange(event);
              }}
              onEnterPress={(input) => {
                if (workerStationInputRef.current) {
                  workerStationInputRef.current.focus();
                } else {
                  input.blur();
                }
              }}
            />
          )}
        />
      </Stack>

      {/* California-specific fields */}
      {isCaliforniaFacility && (
        <>
          <Stack spacing={2}>
            <Text variant="body2">{firstName}&apos;s Station/Wing/Unit/Floor*</Text>
            <Controller
              name="workerStation"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  inputRef={workerStationInputRef}
                  placeholder="e.g. 2nd floor"
                  disabled={signaturePadIsDisabled}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    clearSignature();
                    field.onChange(event);
                  }}
                  onEnterPress={() => {
                    workerNursingAssignmentInputRef.current?.focus();
                  }}
                />
              )}
            />
          </Stack>

          <Stack spacing={2}>
            <Text variant="body2">{firstName}&apos;s nursing services assignment*</Text>
            <Controller
              name="workerNursingAssignment"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  inputRef={workerNursingAssignmentInputRef}
                  placeholder="e.g. Acute care"
                  disabled={signaturePadIsDisabled}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    clearSignature();
                    field.onChange(event);
                  }}
                  onEnterPress={(input) => {
                    input.blur();
                  }}
                />
              )}
            />
          </Stack>
        </>
      )}

      <Stack spacing={2}>
        <Text variant="body2">Workplace employee signature*</Text>
        <Card outlined sx={{ p: 5 }}>
          <Controller
            name="facilityEmployeeSignature"
            control={control}
            render={() => (
              <Box
                aria-label="Workplace employee signature"
                onTouchStartCapture={onDisabledSignaturePadTouchStart}
              >
                <Box sx={{ pointerEvents: signaturePadIsDisabled ? "none" : "all" }}>
                  <SignaturePad ref={signaturePadRef} redrawOnResize height={180} />
                </Box>
              </Box>
            )}
          />
          <Divider sx={{ borderColor: (theme) => theme.palette.border?.primary, mb: 4 }} />
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <TextButton
              underline={false}
              disabled={signaturePadIsDisabled}
              LinkComponent="button"
              onClick={clearSignature}
            >
              Clear
            </TextButton>
          </Box>
        </Card>
      </Stack>

      <LoadingButton
        fullWidth
        type="submit"
        size="large"
        variant="brand"
        disabled={!isValid}
        isLoading={signaturePadIsDisabled}
        sx={{
          mt: 6,
          mb: 6,
          "&.Mui-disabled": {
            backgroundColor: (theme) => theme.palette.grey[300],
            color: (theme) => theme.palette.grey[500],
          },
        }}
        onClick={onSubmit}
      >
        {submitButtonText}
      </LoadingButton>
    </Stack>
  );
});

FacilitySignatureSection.displayName = "FacilitySignatureSection";
